import { useQueryClient } from "@tanstack/react-query";
import { recordsKeys } from "./use-records";
import { invalidateAllRecordCaches } from "./use-cache-invalidation";
import { projectRecordsKeys } from "./project-records-keys";
import { invalidateAskProjectHoverCache } from "@/lib/ask-project-hover-cache";

/**
 * Хук для обновления данных отчетов через React Query
 * Инвалидирует кэш и заставляет компоненты перезагрузить данные
 */
export function useReportReload() {
  const queryClient = useQueryClient();

  /**
   * Обновляет данные конкретного отчета
   * @param recordId - ID записи для обновления
   */
  const reloadSpecificRecord = async (recordId: string) => {
    try {
      // Используем универсальную функцию для инвалидации всех кэшей записи
      // recordId всегда приватный в контексте useReportReload
      await invalidateAllRecordCaches(queryClient, recordId, false);
    } catch (error) {
      console.error(
        "useReportReload: Failed to reload specific record:",
        error
      );
      throw error;
    }
  };

  /**
   * Обновляет все данные отчетов
   */
  const reloadAllReports = async () => {
    try {
      // reports ключ больше не существует

      // Инвалидируем детали всех записей
      await queryClient.invalidateQueries({
        queryKey: recordsKeys.details(),
      });

      // Инвалидируем список записей
      await queryClient.invalidateQueries({
        queryKey: recordsKeys.lists(),
      });

      // Инвалидируем списки записей по проектам для Ask Project hover.
      await queryClient.invalidateQueries({
        queryKey: projectRecordsKeys.all,
        exact: false,
      });

      invalidateAskProjectHoverCache();

      // reports ключ больше не существует, details и lists остаются
    } catch (error) {
      console.error("useReportReload: Failed to reload all reports:", error);
      throw error;
    }
  };

  /**
   * Универсальная функция для обновления данных
   * @param recordId - ID записи для обновления (опционально)
   */
  const reloadReport = async (recordId?: string) => {
    if (recordId) {
      return await reloadSpecificRecord(recordId);
    } else {
      return await reloadAllReports();
    }
  };

  return {
    reloadReport,
    reloadSpecificRecord,
    reloadAllReports,
  };
}
